import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { Form, type FormInstance, Select } from 'antd'
import { useMemo, useState } from 'react'

import type { EquipmentChangeFormValues, SelectedTrailerConfig } from '../types'
import { TractorIcon, TrailerIcon } from '@/components/_SiteWide/TruckIcons'
import { deserializeTrailerValue, getEquipmentChangeFormInitialValues, serializeTrailerValue } from '../utils'

type EquipmentChangeFormProps = {
  handleClose: () => void
  handleEquipmentChange: (values: { tractor_number?: string } & SelectedTrailerConfig, scheduleId: string) => void
  trailersWithConfigs: [string, string][]
  tractorNumbers: string[]
  schedule: Gravitate.Schedule
}

export function EquipmentChangeForm({
  handleClose,
  handleEquipmentChange,
  trailersWithConfigs,
  tractorNumbers,
  schedule,
}: EquipmentChangeFormProps) {
  const currentTractor = schedule.current_tractor_number || schedule.driver_tractor_number
  const currentTrailer = [
    schedule.current_trailer_number || schedule.driver_trailer_number,
    schedule.current_trailer_config || schedule.driver_trailer_type || schedule.default_trailer_config,
  ]
  const [form] = Form.useForm()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const trailerOptions = useMemo(
    () =>
      (trailersWithConfigs || [])
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map((trailer) => ({
          label: `${trailer[0]} (${trailer[1]})`,
          value: serializeTrailerValue([trailer[0], trailer[1]]),
        })),
    [trailersWithConfigs]
  )
  const tractorOptions = useMemo(
    () =>
      (tractorNumbers || [])
        .sort((a, b) => a.localeCompare(b))
        .map((tractor) => ({
          label: tractor,
          value: tractor,
        })),
    [tractorNumbers]
  )

  const initialValues = getEquipmentChangeFormInitialValues(currentTractor, currentTrailer)

  function handleFinish(formValues: EquipmentChangeFormValues) {
    /** modify to handle multiple trailer configurations */
    const trailerConfigs = deserializeTrailerValue(formValues.trailer_ids?.[0]?.trailer_config)
    handleEquipmentChange({ ...trailerConfigs, tractor_number: formValues.tractor_number }, schedule.id)
    handleClose()
  }

  async function checkOneValueExists() {
    if (!!form.getFieldValue('tractor_number') || !!form.getFieldValue('trailer_ids')?.[0]?.trailer_config) {
      return Promise.resolve()
    }
    setErrorMessage('Please change at least one value to proceed.')
    return Promise.reject()
  }

  return (
    <Form
      form={form}
      onFinish={handleFinish}
      onReset={handleClose}
      validateMessages={{ required: 'Required' }}
      initialValues={initialValues}
    >
      <Vertical gap={12} className='pt-2'>
        <Vertical>
          <Horizontal verticalCenter gap={4}>
            <TractorIcon size={15} />
            <Texto category='p1' weight='bold' className='mb-1'>
              Tractor
            </Texto>
          </Horizontal>
          <Form.Item
            name='tractor_number'
            rules={[{ required: true, validator: checkOneValueExists }]}
            required
            noStyle
          >
            <Select placeholder='Tractor Number' options={tractorOptions} showSearch allowClear />
          </Form.Item>
        </Vertical>
        <Form.List name='trailer_ids'>
          {(fields, { add, remove }) => (
            <div>
              {fields.map((field, index) => (
                <Vertical key={field.key}>
                  <Horizontal verticalCenter gap={4}>
                    <TrailerIcon size={15} />
                    <Texto category='p1' weight='bold' className='mb-1'>
                      Trailer
                    </Texto>
                  </Horizontal>
                  <Horizontal>
                    <Form.Item
                      key={field.key}
                      name={[field.name, 'trailer_config']}
                      noStyle
                      rules={[{ required: true, validator: checkOneValueExists }]}
                    >
                      <Select
                        placeholder='Trailer Number'
                        options={trailerOptions}
                        style={{ display: 'flex', flex: 1 }}
                        showSearch
                        allowClear
                      />
                    </Form.Item>
                    {index > 0 && (
                      <GraviButton
                        icon={<IconTrash size={18} />}
                        onClick={() => remove(index)}
                        className='square-ish ml-2'
                        error
                      />
                    )}
                  </Horizontal>
                </Vertical>
              ))}
              {/* UNCOMMENT THIS BUTTON TO ALLOW ADDING MULTIPLE TRAILER IDs */}
              {/* <GraviButton
                icon={<IconPlus size={12} className='mr-1 mt-1' />}
                buttonText='Add Trailer'
                onClick={add}
                className='square-ish mr-3'
                style={{ width: '100px' }}
              /> */}
            </div>
          )}
        </Form.List>
        {errorMessage && (
          <Texto category='p2' appearance='error'>
            {errorMessage}
          </Texto>
        )}
        <Horizontal justifyContent='flex-end' className='pt-2' gap={4}>
          <GraviButton buttonText='Cancel' htmlType='reset' appearance='link' />
          <GraviButton theme2 buttonText='Save' htmlType='submit' />
        </Horizontal>
      </Vertical>
    </Form>
  )
}
